import videojs, { VideoJsPlayer } from 'video.js';

import QualityLevelMenuButton, {
  MenuItemOptions,
} from '@src/components/QualityLevelsMenuButton';
import { Event } from '@src/types/event';
import { QualityLevel } from '@src/types/qualityLevels';
import logger from '@src/utils/logger';
import { attachPluginToPlayer } from '@src/utils/plugin';

const Plugin = videojs.getPlugin('plugin');

export const PLUGIN_NAME = 'qualityLevelsManagerPlugin';

export interface Options {
  placementIndex?: number;
}

class QualityLevelsManagerPlugin extends Plugin {
  vjsPlayer: VideoJsPlayer;
  options: Options;
  menuButton?: QualityLevelMenuButton;

  constructor(player: VideoJsPlayer, options: Options = {}) {
    super(player, options);

    this.vjsPlayer = player;
    this.options = options;

    attachPluginToPlayer(this, player);

    this.player.ready(() => {
      player.addClass('vjs-quality-levels-manager');
      player.trigger(Event.X_PLUGININIT_QUALITYLEVELSMANAGERPLUGIN);

      if (this.vjsPlayer.qualityLevels && this.getVhs()) {
        this.createUI();
        this.setupQualityLevelsHandler();
      }
    });
  }

  createUI() {
    this.menuButton = new QualityLevelMenuButton(this.vjsPlayer);

    const placementIndex = this.vjsPlayer.controlBar.children().length - 2;
    this.vjsPlayer.controlBar.addChild(
      this.menuButton,
      {},
      this.options.placementIndex ?? placementIndex
    );

    this.vjsPlayer.controlBar.qualityLevelMenuButton = this.menuButton;
  }

  getQualityLevelList() {
    return this.vjsPlayer.qualityLevels?.();
  }

  setupQualityLevelsHandler() {
    this.getQualityLevelList()?.on(
      [
        // 'change',
        'addqualitylevel',
        'removequalitylevel',
      ],
      this.handleQualityLevelsAddOrRemove.bind(this)
    );
  }

  getMenuItemOptionFromQualityLevelValue(levelOrAuto: 'auto' | QualityLevel) {
    const value =
      levelOrAuto === 'auto'
        ? ('auto' as const)
        : this.getPixelsFromQualityLevel(levelOrAuto);

    return {
      label: value === 'auto' ? this.vjsPlayer.localize('Auto') : `${value}p`,
      value,
      selectable: true as const,
      multiSelectable: false as const,
    };
  }

  getPixelsFromQualityLevel(level: QualityLevel) {
    const { width, height } = level;
    const pixels = width > height ? height : width;

    return pixels;
  }

  handleQualityLevelsAddOrRemove() {
    const qualityLevelList = this.getQualityLevelList();

    if (!qualityLevelList) return;

    const levels = qualityLevelList.levels_ || [];

    const menuItemsOptionsMap = new Map<number | 'auto', MenuItemOptions>();

    const isAutoOptionExist = levels.length > 1;

    let isAutoSelected = isAutoOptionExist;

    for (let i = 0; i < levels.length; ++i) {
      const level = levels[i];
      const { enabled } = level;

      const pixels = this.getPixelsFromQualityLevel(level);

      if (!enabled) {
        isAutoSelected = false;
      }

      if (!pixels || menuItemsOptionsMap.has(pixels)) {
        continue;
      }

      menuItemsOptionsMap.set(pixels, {
        ...this.getMenuItemOptionFromQualityLevelValue(level),
        selected: enabled, // will be overrided as false below if isAutoSelected is true
      });
    }

    if (isAutoOptionExist) {
      menuItemsOptionsMap.set('auto', {
        ...this.getMenuItemOptionFromQualityLevelValue('auto'),
        selected: true,
      });
    }

    const menuItemsOptions = [...menuItemsOptionsMap]
      .sort(([keyA, itemA], [keyB, itemB]) => {
        if (itemA.value === 'auto') return -1;
        if (itemB.value === 'auto') return 1;
        if (itemA.value > itemB.value) return -1;
        if (itemA.value < itemB.value) return 1;
        return 0;
      })
      .map(([pixels, option]) => {
        return {
          ...option,
          selected:
            option.value === 'auto'
              ? isAutoSelected
              : isAutoSelected
              ? false
              : option.selected,
        };
      });

    if (this.menuButton) {
      this.menuButton.updateItems(menuItemsOptions);
    }
  }

  setQualityLevel(value: number | 'auto') {
    logger.log(`Video quality set to: ${value}`);

    const qualityLevelList = this.getQualityLevelList();

    if (!qualityLevelList) return;

    const levels = qualityLevelList.levels_ || [];

    for (let i = 0; i < levels.length; ++i) {
      const pixels = this.getPixelsFromQualityLevel(levels[i]);

      levels[i].enabled = pixels === value || value === 'auto';
    }
  }

  getVhs() {
    const tech = this.vjsPlayer.tech(
      'tech_wont_be_used_directly_so_suppress_the_warning'
    );

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return tech?.vhs || tech?.hls;
  }
}

videojs.registerPlugin(PLUGIN_NAME, QualityLevelsManagerPlugin);

export type QualityLevelsManagerPluginFactoryFn = (
  options?: Options
) => QualityLevelsManagerPlugin;

export default QualityLevelsManagerPlugin;
